"use client";

import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { SmoothHashAnchor } from "@/components/marketing/SmoothHashAnchor";
import { Button } from "@/components/ui/button";

export const landingNavLinks = [
  { label: "Features", href: "#features" },
  { label: "Generate", href: "#generator" },
  { label: "Autopilot", href: "#autopilot" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
] as const;

function MarketingSectionLink({
  fragment,
  className,
  children,
}: {
  fragment: (typeof landingNavLinks)[number]["href"];
  className?: string;
  children: ReactNode;
}) {
  const pathname = usePathname();
  if (pathname === "/") {
    return (
      <SmoothHashAnchor href={fragment} className={className}>
        {children}
      </SmoothHashAnchor>
    );
  }
  return (
    <Link href={`/${fragment}`} className={className}>
      {children}
    </Link>
  );
}

export function LandingMarketingHeader() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-[padding] duration-300 ease-out ${
        isScrolled ? "md:pt-3" : "md:pt-0"
      }`}
    >
      <div
        className={`flex items-center justify-between gap-6 py-4 transition-all duration-300 ease-out ${
          isScrolled
            ? "w-full border-b border-white/6 bg-[#07070c]/86 px-4 backdrop-blur-xl md:mx-auto md:w-[min(1240px,calc(100vw-56px))] md:rounded-[48px] md:border md:border-white/10 md:bg-[#07070c]/50 md:px-5 md:shadow-[0_10px_34px_rgba(0,0,0,0.42)]"
            : "w-full border-b border-white/6 bg-[#07070c]/72 px-4 md:px-7"
        }`}
      >
        <Link href="/" className="flex items-center gap-3">
          <Image
            src="/brand/logo-transparent.png"
            alt="ReelMagic"
            width={36}
            height={36}
            className="size-9"
          />
          <span className="font-display text-xl tracking-[-0.04em] text-white">
            ReelMagic
          </span>
        </Link>

        <nav className="hidden items-center gap-8 text-sm text-white/70 lg:flex">
          {landingNavLinks.map((link) => (
            <MarketingSectionLink
              key={link.href}
              fragment={link.href}
              className="transition hover:text-white"
            >
              {link.label}
            </MarketingSectionLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 md:gap-3">
          <Link
            href="/login"
            prefetch={false}
            className="hidden text-sm text-white/72 transition hover:text-white md:inline-flex"
          >
            Sign In
          </Link>
          <Button
            asChild
            variant="aura"
            size="lg"
            className="rounded-full px-6"
          >
            <Link href="/login" prefetch={false}>
              Start Free
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

export function LandingMarketingFooter() {
  return (
    <footer className="border-t border-white/6">
      <div className="mx-auto w-[min(1240px,calc(100vw-32px))] py-12 md:w-[min(1240px,calc(100vw-56px))] md:py-16">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-14 xl:gap-20">
          <div className="max-w-md lg:max-w-none">
            <div className="flex items-center gap-3">
              <Image
                src="/brand/logo-transparent.png"
                alt="ReelMagic"
                width={36}
                height={36}
                className="size-9"
              />
              <span className="font-display text-xl tracking-[-0.04em] text-white">
                ReelMagic
              </span>
            </div>
            <p className="mt-4 text-sm leading-6 text-white/54">
              ReelMagic helps creators turn ideas and faceless video formats
              into post-ready videos for TikTok, Reels, and Shorts.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-3 lg:justify-end">
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-wide text-white">
                Product
              </div>
              <nav
                className="mt-4 grid gap-3 text-sm text-white/56"
                aria-label="Product"
              >
                <MarketingSectionLink
                  fragment="#features"
                  className="transition-colors hover:text-white/80"
                >
                  Features
                </MarketingSectionLink>
                <MarketingSectionLink
                  fragment="#pricing"
                  className="transition-colors hover:text-white/80"
                >
                  Pricing
                </MarketingSectionLink>
                <MarketingSectionLink
                  fragment="#faq"
                  className="transition-colors hover:text-white/80"
                >
                  FAQ
                </MarketingSectionLink>
              </nav>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-wide text-white">
                Company
              </div>
              <nav
                className="mt-4 grid gap-3 text-sm text-white/56"
                aria-label="Company"
              >
                <Link
                  href="/support"
                  className="transition-colors hover:text-white/80"
                >
                  Support
                </Link>
              </nav>
            </div>
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <div className="text-sm font-semibold tracking-wide text-white">
                Legal
              </div>
              <nav
                className="mt-4 grid gap-3 text-sm text-white/56"
                aria-label="Legal"
              >
                <Link
                  href="/privacy"
                  className="transition-colors hover:text-white/80"
                >
                  Privacy
                </Link>
                <Link
                  href="/terms"
                  className="transition-colors hover:text-white/80"
                >
                  Terms
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
